import { useEffect } from 'react';
import { EVENT_KEY } from '../../types/enums';

/**
 * @description Freezes the window at a specific vertical scroll position
 * without relying on CSS (no overflow hidden). It blocks user-initiated
 * scrolling such as mouse wheel, touchmove, and keyboard navigation.
 *
 * @param lockPositionY The Y-position at which to freeze the window (default: current scrollY).
 * @param deps Optional dependencies to reapply effect.
 */

const useFreezeWindowScroll = (deps?: ReadonlyArray<unknown>): void => {
  const topPosition = window?.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

  const isInsideScrollable = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) {
      return false;
    }
    return target.closest('[data-allow-scroll="true"]') !== null;
  };

  const lockScroll = (e: Event) => {
    if (isInsideScrollable(e.target)) {
      return;
    } // allow popup scroll
    e.preventDefault();
    window.scrollTo(0, topPosition);
  };

  const blockKeys = (e: KeyboardEvent) => {
    const keys = [
      EVENT_KEY.ARROW_UP,
      EVENT_KEY.ARROW_DOWN,
      EVENT_KEY.PAGE_UP,
      EVENT_KEY.PAGE_DOWN,
      EVENT_KEY.HOME,
      EVENT_KEY.END,
      EVENT_KEY.SPACE,
    ];
    if (keys.includes(e.key as EVENT_KEY) && !isInsideScrollable(e.target)) {
      e.preventDefault();
      window.scrollTo(0, topPosition);
    }
  };

  useEffect(() => {
    window.scrollTo(0, topPosition);

    window.addEventListener('wheel', lockScroll, { passive: false });
    window.addEventListener('touchmove', lockScroll, { passive: false });
    window.addEventListener('keydown', blockKeys, { passive: false });

    return () => {
      window.removeEventListener('wheel', lockScroll);
      window.removeEventListener('touchmove', lockScroll);
      window.removeEventListener('keydown', blockKeys);
    };
  }, deps);
};

export default useFreezeWindowScroll;
